import React, { useState, useContext } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import PropTypes from 'prop-types';

import { object, string, ValidationError } from 'yup';

import Axios from '../../../Assets/Axios/Axios';

import ToastContext from '../../../Context/ToastContext';

import { COLORS } from '../../../Constant/COLORS';

import CardLayout from '../../../Components/CardLayout';
import { TypoPoppBlue, TypoPoppins } from '../../../Components/Typo';

import { getClearedErrorObj } from '../../../Helpers/index';

import { CreateReview } from '../../../Server/Mutation';

const ReviewManageCard = ({
  orderId,
  serviceId,
  servicePackageId,
  professionalName,
  referenceId,
  date,
  updateParentReviews,
}) => {
  const { showToastMessage } = useContext(ToastContext);

  const [formData, setFormData] = useState({ rating: '', review: '' });
  const [errors, setErrors] = useState({ rating: null, review: null });

  function handleChange(event) {
    const { value, name } = event.target;
    setFormData(currentState => ({ ...currentState, [name]: value }));
  }

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      const validatedInputs = await schema.validate(formData, {
        abortEarly: false,
      });
      setErrors(currentErrors => getClearedErrorObj(currentErrors));

      const { errors } = await Axios(CreateReview({
          orderId,
          serviceId,
          servicePackageId,
          rating: validatedInputs.rating,
          description: validatedInputs.review,
        }));

      if (errors) {
        showToastMessage("We're sorry, we couldn't post your review");
        return undefined;
      }

      showToastMessage('Review added successfully', 'success');
      updateParentReviews?.();
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(currentErrors => ({
          ...getClearedErrorObj(currentErrors),
          ...error.inner.reduce((errorObj, error) => {
            if (error.path) errorObj[error.path] = error.message;
            return errorObj;
          }, {}),
        }));
        return undefined;
      }
      showToastMessage('Something went wrong while trying to add the review');
    }
  };

  function handleClear() {
    setErrors(currentErrors => getClearedErrorObj(currentErrors));
    setFormData({ rating: '', review: '' });
  }

  return (
    <CardLayout style={{ width: '100%' }}>
      <form onSubmit={handleSubmit}>
        <Box
          display='grid'
          gridTemplateColumns={{ xs: 'auto', sm: 'auto 1fr' }}
          rowGap={{ xs: 1, sm: 2 }}
          columnGap={3}
        >
          <TypoPoppBlue
            variant='h6'
            fontWeight={600}
            gridColumn={{ sm: 'span 2' }}
          >
            {professionalName}
          </TypoPoppBlue>

          <TypoPoppins>Reference ID</TypoPoppins>
          <TypoPoppins fontWeight={600}>{referenceId}</TypoPoppins>

          <TypoPoppins>Event Date</TypoPoppins>
          <TypoPoppins fontWeight={600}>{date}</TypoPoppins>

          <TypoPoppins mt={{ sm: 1.5 }}>Overall comments</TypoPoppins>
          <FormControl sx={{ minWidth: 250 }}>
            <TextField
              fullWidth
              select
              name='rating'
              size='small'
              value={formData.rating}
              onChange={handleChange}
              InputProps={{ sx: { borderRadius: 3, bgcolor: 'white' } }}
            >
              {['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'].map(
                (item, index) => (
                  <MenuItem value={index + 1} key={item}>
                    {item}
                  </MenuItem>
                )
              )}
            </TextField>
            {errors.rating && (
              <FormHelperText error>{errors.rating}</FormHelperText>
            )}
          </FormControl>

          <TypoPoppins mt={{ sm: 1.5 }}>Review</TypoPoppins>
          <Box>
            <TextField
              spellCheck={false}
              value={formData.review}
              helperText={
                errors.review ||
                '*Make sure review is between 50 to 150 characters'
              }
              error={Boolean(errors.review)}
              name='review'
              onChange={handleChange}
              multiline
              fullWidth
              rows={3}
              InputProps={{
                sx: { bgcolor: 'white', borderRadius: '12px' },
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ alignSelf: 'end', mb: 1 }}
                  >
                    <Typography>{formData.review.length || ''}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <Box
          mt={2}
          justifyContent='end'
          display='grid'
          gridTemplateColumns='repeat(2,minmax(min-content,100px))'
          gap={1}
          alignItems='center'
        >
          <Button
            onClick={handleClear}
            size='small'
            variant='outlined'
            fullWidth
            color='blue'
          >
            <Typography
              color={COLORS.blue}
              variant='caption'
              lineHeight='unset'
            >
              Clear
            </Typography>
          </Button>
          <Button size='small' color='blue' fullWidth type='submit'>
            <Typography color='white' variant='caption' lineHeight='unset'>
              Save
            </Typography>
          </Button>
        </Box>
      </form>
    </CardLayout>
  );
};

const schema = object({
  rating: string().required().label('Overall Comments'),
  review: string()
    .required()
    .min(50, 'Review should be at least 50 characters')
    .max(150, 'Review should be at most 150 characters')
    .label('Review'),
});

ReviewManageCard.propTypes = {
  profFullName: PropTypes.string,
  date: PropTypes.string,
  jobID: PropTypes.string,
  serviceID: PropTypes.string,
  userID: PropTypes.string,
  userFullName: PropTypes.string,
  updateParentReviews: PropTypes.func,
};

export default ReviewManageCard;
